/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React, { useEffect, useMemo, useState } from "react";
import BreakdownCharts from "./BreakdownCharts";
import { Table, Space, Layout, Tooltip, Typography, Skeleton } from "antd";
import "../../Styles/styles-Breakdown.css";
import { formattedPlayerName } from "../../Utils/name_utils";
import { 
  getBarColorsForHighcharts,
} from '../../Utils/chart_utils';
import { useSelector } from "react-redux";
import { trainingAPI } from "@api/TrainingAPI";
import { healthyMean, healthySum } from "../../Utils/injury.mjs";

function round(num) {
  return Math.round((num + Number.EPSILON) * 10) / 10;
}
function round2(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

const { Text } = Typography;

export function BreakdownTable(props) {
  const [tableData, setTableData] = useState(null);
  const [playerStatusData, setPlayerStatusData] = useState({})
  const [playerStatusLoading, setPlayerStatusLoading] = useState(true)

  useEffect(() => {
    if (props.data && props.data?.data && props.data?.metrics && props.data?.wellness_metrics) {
      let tableDataTemp = [...props.data.data];

      tableDataTemp = tableDataTemp.map((e, idx) => {
        const rowData = {
          ...e,
          "key": `${e.player_id}`,
          "id": idx
        }

        if ("drill_data" in e && e["drill_data"].length > 0) {
          rowData["children"] = (e["drill_data"] || []).map((drill, drillIdx) => {
            return {...drill, name: drill.drill_name, key: `${e.player_id}-${drill.drill_name}-${drillIdx}`}
          })
        }
        
        return rowData
      })

      const totalTopSpeed = healthySum(props.data["data"], (row) => topSpeedPercent(row));
      const totalTopSpeedPercent = totalTopSpeed === null ? null : round(totalTopSpeed);

      tableDataTemp.sort((a, b) => a.order - b.order)

      const newTotalRowObj = [...props.data["metrics"], ...props.data["wellness_metrics"], { key: "total_time" }].reduce(
        (acc, { key }) => {
          const filteredData = props.data["data"]?.filter((row) => EXCLUDE_0_GPS_AVG_METRICS.includes(key) ? row[key] !== 0 : (row[key] !== undefined && row[key] !== null));
          
          const total = healthySum(filteredData, (curr) => curr[key]);
          acc[key] = total === null ? null : round(total);
          return acc;
        },
        { calculated_top_speed_percent: totalTopSpeedPercent }
      );

      
      newTotalRowObj["name"] = <span style={{fontWeight: 480}}>Total</span>;
      newTotalRowObj["total_time"] = <span style={{fontWeight: 480}}>{newTotalRowObj["total_time"]}</span>;
      
      setTableData([newTotalRowObj, ...tableDataTemp]);
    }
  }, [])

  useEffect(() => {
    if ((props.data?.data || []).length > 0) {
      (async () => {
        setPlayerStatusData(await trainingAPI.getAllPlayerStatus(undefined, props.selectedDate, 1))
        setPlayerStatusLoading(false)
      })()
    }
  }, [props.data?.data])

  const {
    info: {
      EXCLUDE_0_GPS_AVG_METRICS
    }
  } = useSelector(({ team }) => team);

  const topSpeedPercent = (row) => {
    const div = props.data.player_colors.find(
      (e) => e.player_id === row.player_id
    )?.["top_speed"];
    const topSpeed = row["top_speed"];
    return (div && topSpeed != null && topSpeed !== 0) ? (100 * topSpeed) / div : null;
  };

  const averages = useMemo(() => {
    const meanTopSpeed = healthyMean(props.data["data"], (row) => topSpeedPercent(row));
    const avgTopSpeedPercent = meanTopSpeed === null ? null : round(meanTopSpeed);

    return [...props.data["metrics"], ...props.data["wellness_metrics"], { key: "total_time" }].reduce(
      (acc, { key }) => {
        const filteredData = props.data["data"]?.filter((row) => EXCLUDE_0_GPS_AVG_METRICS.includes(key) ? row[key] !== 0 : (row[key] !== undefined && row[key] !== null));
        
        const avg = healthyMean(filteredData, (curr) => curr[key]);
        acc[key] = avg === null ? null : round(avg);
        return acc;
      },
      { calculated_top_speed_percent: avgTopSpeedPercent !== null ? avgTopSpeedPercent + "%" : "" }
    );
  }, [props.data]);

  const renderColumn =
    (columnKey = "calculated_top_speed_percent") =>
    (value_in, row) => {
      if (!value_in && columnKey !== "calculated_top_speed_percent") {
        return null
      }
      
      let percentValue = null;
      let key = columnKey;

      if (columnKey.endsWith("RPE") && (value_in === undefined)) {
        return ""
      }

      if (columnKey === "calculated_top_speed_percent") {
        percentValue = topSpeedPercent(row);
        key = "top_speed";
        if (percentValue == null) {
          return "";
        }
      }

      let content =
        percentValue == null ? round(value_in) : round(percentValue) + "%";
      if (props.team_name === "Purdue Football" && columnKey === "top_speed") {
        content = round2(value_in);
      }

      let html, style;
      if (typeof row?.name === 'string') {
        style = {};
        const barColor = getBarColorsForHighcharts(row, key, null, null, props.dataColorUtil, row.player_id)
        style["color"] = barColor;
        html = <span style={style}>{content}</span>;
      }
      else {
        style = {};
        style["fontWeight"] = 480;
        html = <span style={style}>{content}</span>;
      }

      return html;
    };

  let columns = [
    {
      title: "Name",
      key: "name",
      children: [
        {
          title: "Average",
          dataIndex: "name",
          render: (_, data) => {
            const name = data.name
            const player_id = data.player_id;

            let status = undefined
            if (playerStatusData && player_id && player_id in playerStatusData) {
              let allPlayerStatus = playerStatusData[player_id]?.status
              status = Object.values(allPlayerStatus)[0]
            }

            const shouldShowStatus = typeof formattedPlayerName(props?.team_name, name) === 'string'
            const isChild = typeof formattedPlayerName(props?.team_name, name) === 'string' && !('drill_data' in data)
            
            return (
              <div style={{display: shouldShowStatus && 'flex', 'alignItems': 'center', "textAlign": isChild && "left"}}>
                {shouldShowStatus && playerStatusLoading && !isChild && 
                  <Skeleton.Avatar active={true} size={'small'} shape="circle" style={{
                        height: '10px',
                        width: '10px',
                        marginRight: '8px',
                        borderRadius: '100%',
                        display: 'flex'
                      }}/>
                }
                {shouldShowStatus && !playerStatusLoading && !isChild &&
                  <Tooltip placement="top" title={
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                      <Text style={{color:"white"}}><Text style={{color:"white"}} strong>Date: </Text>{status?.date || 'No Data'}</Text>
                      <Text style={{color:"white"}}><Text style={{color:"white"}} strong>Status: </Text>{status?.name || props.selectedDate}</Text>
                    </div>
                  }>
                  <span
                    style={{
                      backgroundColor: status?.color || '#f2f2f2',
                      height: '10px',
                      width: '10px',
                      marginRight: '8px',
                      display: 'inline-block',
                      borderRadius: '100%',
                      border: '0.1px solid black'
                    }}
                  />
                </Tooltip>
                }
                {formattedPlayerName(props?.team_name, name)}
              </div>
            )
          },
        },
      ],
      sorter: {
        compare: (a, b) => {
          // keep Total at the top
          const nameA = String(a?.name ?? "").toLowerCase();
          const nameB = String(b?.name ?? "").toLowerCase();

          if (typeof (a?.name ?? "") === "object" || typeof (b?.name ?? "") === "object") {
            return 0; 
          }

          return nameA.localeCompare(nameB);
        },
        multiple: 1,
      },
      render: (name) => (
        <span>{formattedPlayerName(props?.team_name, name)}</span>
      ),
    },
    {
      title: "Pos.",
      key: "position",
      children: [
        {
          title: "",
          dataIndex: "position",
        },
      ],
      sorter: {
        compare: (a, b) => {
          // keep Total at the top
          const nameA = String(a?.position ?? "").toLowerCase();
          const nameB = String(b?.position ?? "").toLowerCase();

          if (typeof (a?.name ?? "") === "object" || typeof (b?.name ?? "") === "object") {
            return 0; 
          }

          return nameA.localeCompare(nameB);
        },
        multiple: 1,
      },
      render: (position) => <span>{position}</span>,
    }
  ];


  if (props.data?.total_time) {
    columns.push({
      title: "Total Time",
      key: "total_time",
      sorter: {
        compare: (a, b) => a?.total_time - b?.total_time,
        multiple: 2,
      },
      children: [
        {
          title: averages["total_time"],
          dataIndex: "total_time",
        },
      ],
      render: (total_time) => <span>{round(total_time)}</span>,
    });
  }

  let top_speed_percent_exists = false;
  if (props.data?.metrics && props.data.metrics.length > 0) {
    top_speed_percent_exists = props.data.metrics.some(item => item.key === "top_speed_percent");
  }
  for (let i = 0; i < props.data.metrics.length; ++i) {
    let metric = props.data.metrics[i];
    let metric_name = metric.name;
    let key = metric.key;

    if (props.hideDistanceZones) {
      if (metric_name === "Distance") {
        metric_name = "Total " + metric_name;
      }
    }
    if (props.team_name === "Orlando Pride") {
      if (metric_name === "Acceleration" || metric_name === "Deceleration") {
        metric_name += " Z4-Z6";
      }
    }

    columns.push({
      title: metric_name,
      dataIndex: key,
      children: [
        {
          title: averages[key],
          dataIndex: key,
          render: renderColumn(key),
        },
      ],
      sorter: {
        compare: (a, b) => a[key] - b[key],
        multiple: columns.length,
      },
    });
    if (key === "top_speed" && !top_speed_percent_exists) {
      columns.push({
        title: "Top Speed %",
        children: [
          {
            title: averages["calculated_top_speed_percent"],
            dataIndex: "calculated_top_speed_percent",
            key: "calculated_top_speed_percent",
            render: renderColumn(),
          },
        ],
        sorter: {
          compare: (a, b) => {
            return topSpeedPercent(a) - topSpeedPercent(b);
          },
          multiple: columns.length,
        },
      });
    }
  }

  return (
    <>
      {(props.data?.data || []).length > 0 ? (
        <>
          <div className="team-table">
            <Space>
              <Table
                rowKey="key"
                columns={
                  columns
                }
                style={{width: '99vw'}}
                scroll={{ x: '99vw' }}
                size="small"
                dataSource={tableData}
                className="exportable gpsdv-table breakdown-table table-scrollbar"
                rowClassName={(row, index) =>
                  props.showAll && "ant-table-row-no-shadow"
                }
                pagination={
                  props.showAll
                    ? false
                    : {
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "30"],
                        position: ["topLeft"],
                      }
                }
                loading={props.loading}
              />
            </Space>
          </div>
          {/* <div className="legend-container">
            <div className="legends-wrapper">
              <span className="legend-icon-green"></span>
              <div> 35% &lt; of Game Baselines</div>
            </div>

            <div className="legends-wrapper">
              <span className="legend-icon-yellow"></span>
              <div> 35-65% of Game Baselines</div>
            </div>

            <div className="legends-wrapper">
              <span className="legend-icon-red"></span>
              <div> &gt;65% of Game Baselines</div>
            </div>
          </div> */}

          <BreakdownCharts
            data={props.chartData}
            hideDistanceZones={props.hideDistanceZones}
            team_name={props.team_name}
            showAll={props.showAll}
            team_colors={props.team_colors}
            dataColorUtil={props.dataColorUtil}
          />
        </>
      ) : (
        <p>No data for your query.</p>
      )}
    </>
  );
}
